/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import type { Area } from 'react-easy-crop/types';

/**
 * Creates a canvas element and crops the image based on the provided crop area.
 *
 * @param imageSrc - The source URL of the image
 * @param cropArea - The crop area from react-easy-crop
 * @param outputSize - The desired output size (width and height)
 * @param outputFormat - The desired output format ('image/jpeg' | 'image/png' | 'image/webp')
 * @param quality - The quality for JPEG output (0-1)
 * @returns Promise<Blob> - The cropped image as a Blob
 */
export const createCroppedImage = async (
  imageSrc: string,
  cropArea: Area,
  outputSize: { width: number; height: number } = { width: 200, height: 200 },
  outputFormat: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg',
  quality: number = 0.9
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Set canvas size to desired output size
      canvas.width = outputSize.width;
      canvas.height = outputSize.height;

      // Draw the cropped image onto the canvas
      ctx.drawImage(
        image,
        cropArea.x, // source x
        cropArea.y, // source y
        cropArea.width, // source width
        cropArea.height, // source height
        0, // destination x
        0, // destination y
        outputSize.width, // destination width
        outputSize.height // destination height
      );

      // Convert canvas to blob
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        outputFormat,
        quality
      );
    };

    image.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Handle CORS for images
    image.crossOrigin = 'anonymous';
    image.src = imageSrc;
  });

/**
 * Converts a Blob to a File with a specified name.
 *
 * @param blob - The blob to convert
 * @param fileName - The desired file name
 * @returns File - The file object
 */
export const blobToFile = (blob: Blob, fileName: string): File =>
  new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });

/**
 * Creates a cropped image file from the original image and crop area.
 *
 * @param imageSrc - The source URL of the image
 * @param cropArea - The crop area from react-easy-crop
 * @param fileName - The desired file name (without extension)
 * @param outputSize - The desired output size
 * @param outputFormat - The desired output format
 * @param quality - The quality for JPEG output (0-1)
 * @returns Promise<File> - The cropped image as a File
 */
export const createCroppedImageFile = async (
  imageSrc: string,
  cropArea: Area,
  fileName: string = 'cropped-image',
  outputSize: { width: number; height: number } = { width: 200, height: 200 },
  outputFormat: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg',
  quality: number = 0.9
): Promise<File> => {
  const blob = await createCroppedImage(
    imageSrc,
    cropArea,
    outputSize,
    outputFormat,
    quality
  );

  // Get file extension based on format
  const extension = outputFormat.split('/')[1];
  const fullFileName = `${fileName}.${extension}`;

  return blobToFile(blob, fullFileName);
};

/**
 * Gets the file extension for a MIME type.
 *
 * @param mimeType - The MIME type
 * @returns string - The file extension
 */
export const getFileExtension = (mimeType: string): string => {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    default:
      return 'jpg';
  }
};

/**
 * Creates a data URL from a File or Blob for preview purposes.
 *
 * @param file - The file or blob
 * @returns Promise<string> - The data URL
 */
export const createImagePreviewUrl = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });

/**
 * Validates if a file is a valid image for cropping.
 *
 * @param file - The file to validate
 * @returns boolean - Whether the file is valid
 */
export const isValidImageFile = (file: File): boolean => {
  const validTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
  ];
  return validTypes.includes(file.type);
};

/**
 * Gets optimal output size for avatars based on the input.
 *
 * @param maxSize - Maximum size for the output
 * @returns object - Width and height for the output
 */
export const getAvatarOutputSize = (
  maxSize: number = 200
): { width: number; height: number } => ({
  width: maxSize,
  height: maxSize,
});

/**
 * Gets optimal output size for logos based on the input.
 *
 * @param maxSize - Maximum size for the output
 * @param aspectRatio - Aspect ratio (width/height)
 * @returns object - Width and height for the output
 */
export const getLogoOutputSize = (
  maxSize: number = 300,
  aspectRatio: number = 1
): { width: number; height: number } => {
  if (aspectRatio >= 1) {
    // Landscape or square
    return {
      width: maxSize,
      height: Math.round(maxSize / aspectRatio),
    };
  }
  // Portrait
  return {
    width: Math.round(maxSize * aspectRatio),
    height: maxSize,
  };
};
